import * as Blockly from 'blockly-tradais';
import { javascriptGenerator } from 'blockly-tradais/javascript';
import { loops } from 'blockly-tradais/blocks';
import { zoneArg, zoneDropdown, ZONE_FIELD } from './time-stamp-healper';

export function registerCustomLoopBlock() {
  // zavo_bug_oct_16: Blockly's break/continue block disables itself (and so emits no code) unless a surrounding
  // block is in this Set; custom_loop emits a real `for(;;)` loop, so it is one. Idempotent (Set.add).
  loops.loopTypes.add('custom_loop');
  Blockly.Blocks['custom_loop'] = {
    init: function () {
      this.appendValueInput('FROM')
        .setCheck('Number')
        .appendField('custom loop from');
      this.appendValueInput('TO')
        .setCheck('Number')
        .appendField('to');
      this.appendValueInput('STEP')
        .setCheck('Number')
        .appendField('step');
      this.appendValueInput('VARIABLE')
        .setCheck('Number')
        .appendField('variable');
      this.appendStatementInput('DO')
        .appendField('do');
      this.setInputsInline(true);
      this.setPreviousStatement(true, null);
      this.setNextStatement(true, null);
      this.setColour('#5CA65C');
      this.setTooltip('Custom loop with start, end, step and index variable assignment');
    }
  };

  (javascriptGenerator as any).forBlock['custom_loop'] = function(block: any) {
    const fromValue = (javascriptGenerator as any).valueToCode(
      block,
      'FROM',
      (javascriptGenerator as any).ORDER_ASSIGNMENT
    ) || '0';
    const toValue = (javascriptGenerator as any).valueToCode(
      block,
      'TO',
      (javascriptGenerator as any).ORDER_ASSIGNMENT
    ) || '0';
    const stepValue = (javascriptGenerator as any).valueToCode(
      block,
      'STEP',
      (javascriptGenerator as any).ORDER_ASSIGNMENT
    ) || '1';
    const doCode = (javascriptGenerator as any).statementToCode(block, 'DO');
    const indexVar = (javascriptGenerator as any).valueToCode(
      block,
      'VARIABLE',
      (javascriptGenerator as any).ORDER_ASSIGNMENT
    ) || '1';
    // Direction-aware loop. A hardcoded `i <= to` made every REVERSE loop (from > to, negative step) never
    // run — `10 <= 0` is false on the first check. Bind FROM/TO/STEP to per-block temps (evaluate each once;
    // the names carry no `$`/`%` so resolveFunctionCode leaves them intact), then pick `<=` for a forward
    // step and `>=` for a reverse one so the loop counts in whichever direction STEP points.
    // ZERO-STEP GUARD: a step of 0 (or NaN/undefined) never advances `i`, so the loop would spin forever
    // (`0 >= 0` → the `i <= to` branch, `i += 0`). NEVER emit that — coerce a falsy step to 1 and warn, so a
    // misconfigured block does nothing worse than a forward walk instead of hanging the tab. See the
    // 2026-09-03 SpeciesNameDropDownList hang.
    const sfx = String(block.id).replace(/[^a-zA-Z0-9_]/g, '_');
    const loopVar = `__loop_i_${sfx}`;
    const toVar = `__loop_to_${sfx}`;
    const stepVar = `__loop_step_${sfx}`;
    return (
      `{\n` +
      `let ${loopVar} = ${fromValue};\n` +
      `const ${toVar} = ${toValue};\n` +
      `let ${stepVar} = ${stepValue};\n` +
      `if (!${stepVar}) { console.warn('custom_loop: step was 0 or invalid — coerced to 1 to avoid an infinite loop'); ${stepVar} = 1; }\n` +
      `for(; ${stepVar} >= 0 ? ${loopVar} <= ${toVar} : ${loopVar} >= ${toVar}; ${loopVar} += ${stepVar}) {\n` +
      `${indexVar} = ${loopVar};\n${doCode}}\n` +
      `}\n`
    );
  };
}

export function registerRegexMatchBlock() {
  Blockly.Blocks['string_regex_match'] = {
    init: function () {
      this.appendValueInput('TEXT')
        .setCheck('String')
        .appendField('string');
      this.appendValueInput('REGEX')
        .setCheck('String')
        .appendField('matches regex');
      this.setOutput(true, 'Boolean');
      this.setColour('#5ba58c');
      this.setTooltip('Returns true if the string matches the regex pattern');
      this.setHelpUrl('');
    },
  };

  (javascriptGenerator as any).forBlock['string_regex_match'] = function (
    block: any
  ) {
    const text = (javascriptGenerator as any).valueToCode(
      block,
      'TEXT',
      (javascriptGenerator as any).ORDER_NONE
    ) || "''";
    const regex = (javascriptGenerator as any).valueToCode(
      block,
      'REGEX',
      (javascriptGenerator as any).ORDER_NONE
    ) || "''";
    const sanitizedRegex = regex.replace(/\$/g, '~');
    const code = `this.testRegex("${sanitizedRegex.replaceAll(/'/g, '')}", ${text})`;
    return [code, (javascriptGenerator as any).ORDER_ATOMIC];
  };
}

export function registerStringContainsBlock() {
  Blockly.Blocks['string_contains'] = {
    init: function () {
      this.appendValueInput('TEXT')
        .setCheck('String')
        .appendField('string');
      this.appendValueInput('SUBSTRING')
        .setCheck('String')
        .appendField('contains');
      this.setOutput(true, 'Boolean');
      this.setColour('#5ba58c');
      this.setTooltip('Returns true if the string contains the substring');
      this.setHelpUrl('');
    },
  };

  (javascriptGenerator as any).forBlock['string_contains'] = function (
    block: any
  ) {
    const text = (javascriptGenerator as any).valueToCode(
      block,
      'TEXT',
      (javascriptGenerator as any).ORDER_NONE
    ) || "''";
    const substring = (javascriptGenerator as any).valueToCode(
      block,
      'SUBSTRING',
      (javascriptGenerator as any).ORDER_NONE
    ) || "''";
    const code = `${text}.includes(${substring})`;
    return [code, (javascriptGenerator as any).ORDER_ATOMIC];
  };
}

/**
 * "is empty" — whether a value is ABSENT (null or undefined).
 *
 * Generates `(VALUE == null)`, deliberately loose: `== null` is true for null AND undefined, which
 * is the pair a builder means by "empty". `=== null` would miss an unset variable.
 *
 * ⚠ IT IS NOT A ZERO TEST. `0 == null` is false and `'' == null` is false, so a deliberate zero and
 * an empty string both read as NOT empty — which is the whole point of null being a separate value.
 *
 * The `logic_compare` + `logic_null` pair produces the same JavaScript and keeps working; this block
 * exists because that pair is not discoverable. Permanent dual representation, like the typed
 * condition blocks.
 */
export function registerValueIsEmptyBlock() {
  Blockly.Blocks['value_is_empty'] = {
    init: function () {
      this.appendValueInput('VALUE')
        .appendField('is empty');
      this.setOutput(true, 'Boolean');
      this.setColour('#5C81A6');
      this.setTooltip('Returns true when the value is empty (null). A zero or an empty text is NOT empty.');
      this.setHelpUrl('');
    },
  };

  (javascriptGenerator as any).forBlock['value_is_empty'] = function (block: any) {
    const value = (javascriptGenerator as any).valueToCode(
      block,
      'VALUE',
      (javascriptGenerator as any).ORDER_NONE
    ) || 'null';
    const code = `(${value} == null)`;
    return [code, (javascriptGenerator as any).ORDER_ATOMIC];
  };
}

export function registerFormatIsoDateBlock() {
  Blockly.Blocks['format_iso_date'] = {
    init: function () {
      this.appendValueInput('FORMAT')
        .setCheck('String')
        .appendField('format ISO date pattern');
      this.appendValueInput('DATE')
        .setCheck('TimeStamp')
        .appendField('iso value');
      this.appendDummyInput().appendField(zoneDropdown(), ZONE_FIELD);
      this.setOutput(true, 'String');
      this.setColour('#F5A623'); // Time Stamp category colour
      this.setTooltip('Format a date into text using the supplied pattern (e.g., DD/MM/YYYY), on the project time zone\'s clock (or UTC, if switched).');
      this.setHelpUrl('');
    },
  };

  (javascriptGenerator as any).forBlock['format_iso_date'] = function (block: any) {
    const format = javascriptGenerator.valueToCode(block, 'FORMAT', 0) || "''";
    const date = javascriptGenerator.valueToCode(block, 'DATE', 0) || "''";
    const code = `this.formatDateByPattern(${format}, ${date}${zoneArg(block)})`;
    return [code, (javascriptGenerator as any).ORDER_ATOMIC];
  };
}

export function registerParseDateToIsoBlock() {
  Blockly.Blocks['parse_date_to_iso'] = {
    init: function () {
      // FORMAT is now a fixed DROPDOWN (2026-09-16) — was a String value socket. The old socket line is
      // kept commented per the no-delete rule. Back-compat: saved functions run their STORED code, so they
      // are unaffected at runtime; only re-saving a block that had a custom (wired) pattern resets it to
      // the dropdown default (a saved <value name="FORMAT"> no longer matches a same-named field on load).
      // this.appendValueInput('FORMAT').setCheck('String').appendField('parse date pattern');
      this.appendDummyInput()
        .appendField('parse date pattern')
        .appendField(new Blockly.FieldDropdown([
          ['2026-12-25  (YYYY-MM-DD)', 'YYYY-MM-DD'],
          ['25/12/2026  (DD/MM/YYYY)', 'DD/MM/YYYY'],
          ['12/25/2026  (MM/DD/YYYY)', 'MM/DD/YYYY'],
          ['25 Dec 2026  (DD MMM YYYY)', 'DD MMM YYYY'],
          ['Dec 25, 2026  (MMM DD, YYYY)', 'MMM DD, YYYY'],
          ['25/12/2026 09:30  (DD/MM/YYYY HH:mm)', 'DD/MM/YYYY HH:mm'],
          ['2026-12-25 09:30  (YYYY-MM-DD HH:mm)', 'YYYY-MM-DD HH:mm'],
        ]), 'FORMAT');
      this.appendValueInput('VALUE')
        .setCheck('String')
        .appendField('date value');
      this.appendDummyInput().appendField(zoneDropdown(), ZONE_FIELD);
      this.setOutput(true, 'TimeStamp');
      this.setColour('#F5A623');
      this.setTooltip('Parse a date written as text into a date value. Pick the pattern that matches the text (e.g. "25/12/2026" → DD/MM/YYYY), then wire in the text. The text is read as project time (or UTC, if switched).');
      this.setHelpUrl('');
    },
  };

  (javascriptGenerator as any).forBlock['parse_date_to_iso'] = function (block: any) {
    // FORMAT is a dropdown field now (a fixed pattern string), VALUE is still the wired text.
    const format = block.getFieldValue('FORMAT') || 'YYYY-MM-DD';
    const value = javascriptGenerator.valueToCode(block, 'VALUE', 0) || "''";
    const code = `this.parseDateByPattern('${format}', ${value}${zoneArg(block)})`;
    return [code, (javascriptGenerator as any).ORDER_ATOMIC];
  };
}

export function registerNumberToWordsBlock() {
  Blockly.Blocks['number_to_words'] = {
    init: function () {
      this.appendValueInput("NUM")
          .setCheck("Number")
          .appendField("number to words");
      this.setOutput(true, "String");
      this.setColour('#5C81A6');
      this.setTooltip("Convert a number to its word equivalent");
      this.setHelpUrl("");
    }
  };

  (javascriptGenerator as any).forBlock['number_to_words'] = function (block: any) {
    const num = javascriptGenerator.valueToCode(block, 'NUM', 0) || "0";
    const code = `this.numberToWords(${num})`;
    return [code, (javascriptGenerator as any).ORDER_ATOMIC];
  };
}

export function registerTrimDecimalsBlock() {
  Blockly.Blocks['trim_decimals'] = {
    init: function () {
      this.appendValueInput('VALUE')
        .setCheck(['Number', 'String'])
        .appendField('round decimals of');
      this.appendValueInput('DIGITS')
        .setCheck('Number')
        .appendField('to digits');
      this.setOutput(true, 'Number');
      this.setColour('#495284');
      this.setTooltip('Round a number to n decimal places');
      this.setHelpUrl('');
    },
  };
  (javascriptGenerator as any).forBlock['trim_decimals'] = (block: any) => {
    var value = (javascriptGenerator as any).valueToCode(block, 'VALUE', (javascriptGenerator as any).ORDER_NONE) || '0';
    var digits = (javascriptGenerator as any).valueToCode(block, 'DIGITS', (javascriptGenerator as any).ORDER_NONE) || '2';
    var code = `this.trimDecimals(${value}, ${digits})`;
    return [code, (javascriptGenerator as any).ORDER_FUNCTION_CALL];
  };
}

export function registerAndConditionBlock() {
  Blockly.Blocks['entity_and_conditions'] = {
    init: function () {
      this.appendStatementInput('CHILD_CONDITIONS')
        .setCheck(null)
        .appendField('AND');
      this.appendStatementInput('SIBILING_CONDITIONS')
        .setCheck(null)
        .appendField('and');
      this.setPreviousStatement(true, null);
      this.setNextStatement(true, null);
      this.setColour('#5C81A6');
      this.setTooltip('Combine multiple conditions with AND');
      this.setHelpUrl('');
    },
  };
  (javascriptGenerator as any).forBlock['entity_and_conditions'] = function (
    block: any
  ) {
    var child = (javascriptGenerator as any).statementToCode(block, 'CHILD_CONDITIONS');
    var sibling = (javascriptGenerator as any).statementToCode(block, 'SIBILING_CONDITIONS');
    var code = '';
    if (child && sibling) {
      code = '(' + child.trim() + ' && ' + sibling.trim() + ')';
    } else if (child) {
      code = '(' + child.trim() + ')';
    } else if (sibling) {
      code = '(' + sibling.trim() + ')';
    }
    return code ? code + '\n' : '';
  };
}

export function registerPolygonAreaBlock() {
  Blockly.Blocks['polygon_area'] = {
    init: function () {
      this.appendValueInput('GEOMETRY')
        // 'String' as well as 'GEOMETRY' on purpose: a builder may keep GeoJSON in a plain STRING column —
        // contract §5 requires no particular type — and must still be able to compute its area.
        .setCheck(['GEOMETRY', 'String'])
        .appendField('area of');
      this.appendDummyInput()
        .appendField('in')
        .appendField(
          new Blockly.FieldDropdown([
            ['hectares', 'hectares'],
            ['acres', 'acres'],
            ['m²', 'sqm'],
            ['km²', 'sqkm'],
          ]),
          'UNIT'
        );
      this.setOutput(true, 'Number');
      // Matches the Maps toolbox category, not Text. See toolbox-xml.ts.
      this.setColour('#00897b');
      this.setTooltip('Area of a GeoJSON polygon. 0 for points, lines, or empty geometry.');
      this.setHelpUrl('');
    },
  };

  (javascriptGenerator as any).forBlock['polygon_area'] = function (block: any) {
    const geo = (javascriptGenerator as any).valueToCode(
      block,
      'GEOMETRY',
      (javascriptGenerator as any).ORDER_NONE
    ) || "''";
    const unit = block.getFieldValue('UNIT') || 'hectares';
    return [`this.polygonArea(${geo}, '${unit}')`, (javascriptGenerator as any).ORDER_ATOMIC];
  };
}

export function registerPointCountBlock() {
  Blockly.Blocks['point_count'] = {
    init: function () {
      this.appendValueInput('GEOMETRY')
        // 'String' as well as 'GEOMETRY' for the same reason as polygon_area: a builder may keep GeoJSON
        // in a plain STRING column, and must still be able to count its points.
        .setCheck(['GEOMETRY', 'String'])
        .appendField('number of points in');
      this.setOutput(true, 'Number');
      // Matches the Maps toolbox category, not Text. See toolbox-xml.ts.
      this.setColour('#00897b');
      this.setTooltip('Number of points in a shape. 0 when nothing is drawn.');
      this.setHelpUrl('');
    },
  };

  (javascriptGenerator as any).forBlock['point_count'] = function (block: any) {
    const geo = (javascriptGenerator as any).valueToCode(
      block,
      'GEOMETRY',
      (javascriptGenerator as any).ORDER_NONE
    ) || "''";
    return [`this.pointCount(${geo})`, (javascriptGenerator as any).ORDER_ATOMIC];
  };
}

export function registerReverseGeocodeBlock() {
  Blockly.Blocks['reverse_geocode'] = {
    init: function () {
      this.appendValueInput('GEOMETRY')
        // 'String' as well as 'GEOMETRY' for the same reason as polygon_area: a builder may keep GeoJSON
        // in a plain STRING column, and must still be able to name the place.
        .setCheck(['GEOMETRY', 'String'])
        .appendField('place name of');
      this.setOutput(true, 'String');
      // Matches the Maps toolbox category, not Text. See toolbox-xml.ts.
      this.setColour('#00897b');
      this.setTooltip('Human-readable place for a geometry, e.g. "Guntur, Andhra Pradesh, India". Empty if it cannot be resolved.');
      this.setHelpUrl('');
    },
  };

  (javascriptGenerator as any).forBlock['reverse_geocode'] = function (block: any) {
    const geo = (javascriptGenerator as any).valueToCode(
      block,
      'GEOMETRY',
      (javascriptGenerator as any).ORDER_NONE
    ) || "''";
    // AWAITED: this one crosses the network, unlike every other value block here. Safe because the
    // generated page function is always `async` (ui-editor.component.ts ~2361).
    return [`(await this.reverseGeocode(${geo}))`, (javascriptGenerator as any).ORDER_ATOMIC];
  };
}

/**
 * check_location_access — ONE statement block that writes up to FOUR variables, same shape as
 * `time_difference` (see time-stamp-healper.ts): each value slot holds a get_variable-family GETTER
 * that the generator ASSIGNS into, and an unconnected slot is simply skipped. No inputs — the reading
 * takes nothing, unlike time_difference's dateA/dateB. Backed by LocationDevice.read(), which never
 * throws and never shows a dialog. Glossary: src/app/shared/geometry/CONTEXT.md, section "Location".
 */
export function registerCheckLocationAccessBlock() {
  Blockly.Blocks['check_location_access'] = {
    init: function () {
      this.appendDummyInput().appendField('check location access');
      this.appendValueInput('allowed').setCheck('Boolean').appendField('allowed →');
      this.appendValueInput('canBeAsked').setCheck('Boolean').appendField('can be asked →');
      this.appendValueInput('refusedForGood').setCheck('Boolean').appendField('refused for good →');
      this.appendValueInput('switchedOff').setCheck('Boolean').appendField('switched off →');
      this.setInputsInline(false);
      this.setPreviousStatement(true, null);
      this.setNextStatement(true, null);
      // Matches the Maps toolbox category. See toolbox-xml.ts.
      this.setColour('#00897b');
      this.setTooltip(
        'Checks whether this app can read the app user\'s location. Never shows a dialog. "allowed" is ' +
        'true when location can be used. "can be asked" is true when "ask for location access" will show ' +
        'the dialog. "refused for good" is true when the device won\'t ask again — show how to turn ' +
        'location on in settings. "switched off" is true when location is off on the phone.'
      );
      this.setHelpUrl('');
    },
  };

  (javascriptGenerator as any).forBlock['check_location_access'] = function (block: any) {
    const g = javascriptGenerator as any;
    // NOTE: must NOT keep '$' (or '%') — resolveFunctionCode() rewrites those as variable-path markers
    // (see the identical comment on time_difference's generator in time-stamp-healper.ts).
    const tmp = '__cla_' + String(block.id).replace(/[^a-zA-Z0-9_]/g, '_');
    const allowedRef = g.valueToCode(block, 'allowed', g.ORDER_ASSIGNMENT);
    const canBeAskedRef = g.valueToCode(block, 'canBeAsked', g.ORDER_ASSIGNMENT);
    const refusedForGoodRef = g.valueToCode(block, 'refusedForGood', g.ORDER_ASSIGNMENT);
    const switchedOffRef = g.valueToCode(block, 'switchedOff', g.ORDER_ASSIGNMENT);
    let code = `const ${tmp} = await this.checkLocationAccess();\n`;
    if (allowedRef && !allowedRef.includes('NAN')) code += `${allowedRef} = ${tmp}.allowed;\n`;
    if (canBeAskedRef && !canBeAskedRef.includes('NAN')) code += `${canBeAskedRef} = ${tmp}.canBeAsked;\n`;
    if (refusedForGoodRef && !refusedForGoodRef.includes('NAN')) code += `${refusedForGoodRef} = ${tmp}.refusedForGood;\n`;
    if (switchedOffRef && !switchedOffRef.includes('NAN')) code += `${switchedOffRef} = ${tmp}.switchedOff;\n`;
    return code;
  };
}

/**
 * ask_location_access — fire-and-forget: shows the device's own location prompt (never a Zavo dialog)
 * and does NOT wait for the answer. Backed by LocationDevice.ask(), which guards its own in-flight
 * state, so the generator does not await it. No slots. Glossary: same file as check_location_access.
 */
export function registerAskLocationAccessBlock() {
  Blockly.Blocks['ask_location_access'] = {
    init: function () {
      this.appendDummyInput().appendField('ask for location access');
      this.setPreviousStatement(true, null);
      this.setNextStatement(true, null);
      // Matches the Maps toolbox category. See toolbox-xml.ts.
      this.setColour('#00897b');
      this.setTooltip(
        'Shows the device\'s "Allow location?" dialog. The function does not wait for the answer — check ' +
        'it later with "check location access", for example when Save is pressed. Does nothing when ' +
        '"refused for good" or "switched off" is true: the device can\'t ask then, so show the app user ' +
        'how to turn location on in their phone\'s settings.'
      );
      this.setHelpUrl('');
    },
  };

  (javascriptGenerator as any).forBlock['ask_location_access'] = function () {
    return 'this.askLocationAccess();\n';
  };
}

export function registerAllMiscBlocks() {
  registerCustomLoopBlock();
  registerRegexMatchBlock();
  registerStringContainsBlock();
  registerValueIsEmptyBlock();
  registerPolygonAreaBlock();
  registerPointCountBlock();
  registerReverseGeocodeBlock();
  registerCheckLocationAccessBlock();
  registerAskLocationAccessBlock();
  registerFormatIsoDateBlock();
  registerParseDateToIsoBlock();
  registerNumberToWordsBlock();
  registerTrimDecimalsBlock();
  registerAndConditionBlock();
}
